import { ComponentChild } from 'preact'

import type { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'

import { Grade, GradeValue } from '@bonniernews/dn-design-system-web/foundations/icons/grade-icon.tsx'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'
import { StringToHtml } from '@bonniernews/dn-design-system-web/helpers/stringToHtml.tsx'

import { ArticleBodyImage } from '@bonniernews/dn-design-system-web/components/article-body-image/article-body-image.tsx'

export interface FactboxProps extends SharedProps {
  title?: string
  titleHighlight?: string
  titleImage?: ComponentChild
  bodyParts: Array<BodyPart>
  theme?: 'default' | 'kultur' | 'nyheter'
}

interface BodyPart {
  type: string
  bodyHtml?: string
  grade?: GradeValue
  media?: ComponentChild
  imageType?: string
  caption?: string
  author?: string
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/factbox](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/factbox)
 * - Storybook: [Factbox](https://designsystem.dn.se/?path=/docs/basic-factbox--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/factbox/factbox.scss'`
 */
export const Factbox = ({
  title,
  titleHighlight,
  titleImage,
  bodyParts,
  theme,
  classNames,
  attributes,
}: FactboxProps) => {
  const classes = formatClassString(['ds-factbox', theme && `ds-theme--${theme}`, classNames])

  return (
    <div className={classes} {...attributes}>
      <div className='ds-factbox__inner'>
        <div className='ds-factbox__content'>
          {titleImage && <div className='ds-factbox__title-image'>{titleImage}</div>}
          {(title || titleHighlight) && (
            <h2 className='ds-factbox__title'>
              {titleHighlight && <span className='ds-factbox__title-highlight'>{titleHighlight}</span>}
              {title}
            </h2>
          )}
          {bodyParts && <BodyParts bodyParts={bodyParts} />}
        </div>
      </div>
    </div>
  )
}

function BodyParts({ bodyParts }: { bodyParts: Array<BodyPart> }) {
  return (
    <div className='ds-factbox__body'>
      {bodyParts.map((part) => {
        if (part.type === 'paragraph' && part.bodyHtml) {
          return <StringToHtml htmlString={part.bodyHtml} />
        } else if (part.type === 'gradeMeter' && part.grade) {
          return <Grade grade={part.grade} type='scale' />
        } else if (part.type === 'image') {
          return (
            <ArticleBodyImage
              image={part.media}
              fullWidth={false}
              imageType={part.imageType}
              caption={part.caption || ''}
              author={part.author || ''}
            />
          )
        } else {
          return null
        }
      })}
    </div>
  )
}

export default Factbox
